import { Box, HStack, Input, Stack, Tabs } from '@chakra-ui/react';
import { Plus } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { StatusBadge, type StatusType } from '~/components/display/StatusBadge';
import { useAppHeader } from '~/components/layout/shared/AppHeaderContext';
import { LAYOUT_SPACING } from '~/components/layout/shared/spacing';
import { ProjectModal } from '~/components/project';
import { type ColumnDefinition, ReusableTable } from '~/components/tables/ReusableTable';
import { PageLoadingSpinner, PrimaryButton } from '~/components/ui';
import { toaster } from '~/components/ui/toaster';
import {
  type ProjectData as StoreProjectData,
  useProjectList,
  useProjectStore,
  useTestingProjectList,
} from '~/hooks/useProjectStore';
import { PROJECT_ROUTES } from '~/pages/project/projectRoutes';
import { useAppStore } from '~/store';
import { CloneProjectModal } from './CloneProjectModal';

/**
 * Determines the best route for a project based on its completeness.
 * - Has configurations with candidates → Proposal page
 * - Has all required inputs (dates, tax, millage, drf) → Proposal page
 * - Otherwise → Setup page
 */
const getSmartRoute = (project: StoreProjectData | undefined): string => {
  if (!project) {
    return PROJECT_ROUTES.SETUP;
  }

  // If project has configurations with candidates, go to Proposal
  if (project.configurations && project.configurations.length > 0) {
    const hasProcessedConfig = project.configurations.some(
      (config) => config.candidates && config.candidates.length > 0
    );
    if (hasProcessedConfig) {
      return PROJECT_ROUTES.PROPOSAL;
    }
  }

  // If project has all required inputs, go to Proposal (ready to add bonds)
  const hasAllInputs =
    project.dateInputs && project.taxValueInputs && project.millageInputs && project.drfInputs;

  if (hasAllInputs) {
    return PROJECT_ROUTES.PROPOSAL;
  }

  // Default to Setup for incomplete projects
  return PROJECT_ROUTES.SETUP;
};

/**
 * Determines the workflow status of a project from metadata fields:
 * - 'New' - No inputs saved yet
 * - 'Inputs' - Some inputs saved but no configurations
 * - 'Proposal' - Has at least one configuration
 */
const getWorkflowStatusFromMetadata = (metadata: {
  hasInputs?: boolean;
  configurationCount?: number;
}): { label: string; type: StatusType } => {
  // Has configurations = Proposal stage
  if (metadata.configurationCount && metadata.configurationCount > 0) {
    return { label: 'Proposal', type: 'active' };
  }

  // Has any inputs = Inputs stage
  if (metadata.hasInputs) {
    return { label: 'Inputs', type: 'due-soon' };
  }

  // Nothing saved yet = New
  return { label: 'New', type: 'default' };
};

// Define the type for project display in the table
interface ProjectData {
  id: string;
  bondName: string;
  clientName: string;
  status: 'active' | 'archived';
  workflowStatus: string;
  workflowStatusType: StatusType;
  configCount: number;
  favoriteCount: number;
  targetCompletionDate?: string;
  createdAt: string;
  lastUpdated: string;
  [key: string]: string | number | unknown;
}

// Define columns configuration
const projectColumns: ColumnDefinition<ProjectData>[] = [
  { key: 'bondName', header: 'Project Name', accessor: 'bondName' },
  { key: 'clientName', header: 'Client', accessor: 'clientName' },
  {
    key: 'workflowStatus',
    header: 'Status',
    accessor: (item) => item.workflowStatus,
    isStatus: true,
    headerTextAlign: 'center',
    cellTextAlign: 'center',
    disableSort: true,
  },
  {
    key: 'configCount',
    header: 'Configs',
    accessor: (item) => String(item.configCount),
    headerTextAlign: 'center',
    cellTextAlign: 'center',
  },
  {
    key: 'favoriteCount',
    header: 'Favorites',
    accessor: (item) => String(item.favoriteCount),
    headerTextAlign: 'center',
    cellTextAlign: 'center',
  },
  {
    key: 'targetCompletionDate',
    header: 'Due Date',
    accessor: (item) => item.targetCompletionDate || '-',
  },
  { key: 'createdAt', header: 'Created', accessor: 'createdAt' },
  { key: 'lastUpdated', header: 'Last Updated', accessor: 'lastUpdated' },
];

const HomePage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTab, setSelectedTab] = useState('active');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editProject, setEditProject] = useState<ProjectData | null>(null);
  const [cloneProject, setCloneProject] = useState<ProjectData | null>(null);
  const navigate = useNavigate();
  const { setHeader } = useAppHeader();

  // Get user info for super admin check
  const userInfo = useAppStore((s) => s.userInfo);
  const isSuperAdmin = userInfo?.isSuperAdmin ?? false;

  // Set header title
  useEffect(() => {
    setHeader({ title: 'Projects Dashboard' });
  }, [setHeader]);

  // Fetch project list from API (auto-fetches on mount)
  const { projectList, hasLoadedList } = useProjectList();
  // Fetch testing projects only if super admin (skips API call for non-admins)
  const { testingProjectList } = useTestingProjectList(isSuperAdmin);
  const deleteProject = useProjectStore((state) => state.deleteProject);
  const deleteTestingProject = useProjectStore((state) => state.deleteTestingProject);
  const archiveProject = useProjectStore((state) => state.archiveProject);
  const unarchiveProject = useProjectStore((state) => state.unarchiveProject);
  // Get cached projects for smart navigation only
  const cachedProjects = useProjectStore((state) => state.projects);

  // Transform project list into display format (memoized to prevent infinite re-renders)
  const projects: ProjectData[] = useMemo(() => {
    return projectList.map((metadata) => {
      // Use metadata fields from backend (computed by backend)
      const { label: workflowStatus, type: workflowStatusType } =
        getWorkflowStatusFromMetadata(metadata);

      return {
        id: metadata.id,
        bondName: metadata.name,
        clientName: metadata.clientName || '-',
        status: metadata.archivedAt ? ('archived' as const) : ('active' as const),
        workflowStatus,
        workflowStatusType,
        configCount: metadata.configurationCount ?? 0,
        favoriteCount: metadata.favoriteCount ?? 0,
        targetCompletionDate: metadata.targetCompletionDate
          ? new Date(`${metadata.targetCompletionDate.split('T')[0]}T00:00:00`).toLocaleDateString(
              'en-US',
              {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              }
            )
          : undefined,
        createdAt: new Date(metadata.createdAt).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        }),
        lastUpdated: new Date(metadata.updatedAt).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        }),
        // Keep original ISO dates for edit modal
        _targetCompletionDateISO: metadata.targetCompletionDate,
      };
    });
  }, [projectList]);

  // Transform testing project list (super admin only)
  const testingProjects: ProjectData[] = useMemo(() => {
    return testingProjectList.map((metadata) => {
      const { label: workflowStatus, type: workflowStatusType } =
        getWorkflowStatusFromMetadata(metadata);

      return {
        id: metadata.id,
        bondName: metadata.name,
        clientName: metadata.clientName || '-',
        status: metadata.archivedAt ? ('archived' as const) : ('active' as const),
        workflowStatus,
        workflowStatusType,
        configCount: metadata.configurationCount ?? 0,
        favoriteCount: metadata.favoriteCount ?? 0,
        targetCompletionDate: metadata.targetCompletionDate
          ? new Date(`${metadata.targetCompletionDate.split('T')[0]}T00:00:00`).toLocaleDateString(
              'en-US',
              {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              }
            )
          : undefined,
        createdAt: new Date(metadata.createdAt).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        }),
        lastUpdated: new Date(metadata.updatedAt).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        }),
        _targetCompletionDateISO: metadata.targetCompletionDate,
      };
    });
  }, [testingProjectList]);

  // Action handlers
  const handleEdit = (item: ProjectData) => {
    // Find the original metadata to get the ISO date
    const originalMetadata = projectList.find((p) => p.id === item.id);
    setEditProject({
      ...item,
      targetCompletionDate: originalMetadata?.targetCompletionDate,
    });
  };

  const handleDelete = async (item: ProjectData) => {
    const success = await deleteProject(item.id);
    if (success) {
      toaster.create({
        title: 'Project deleted',
        description: `"${item.bondName}" has been deleted`,
        type: 'success',
      });
    } else {
      toaster.create({
        title: 'Delete failed',
        description: `Failed to delete "${item.bondName}"`,
        type: 'error',
      });
    }
  };

  // Special delete handler for testing projects (uses separate API endpoint)
  const handleValuesTestingDelete = async (item: ProjectData) => {
    const success = await deleteTestingProject(item.id);
    if (success) {
      toaster.create({
        title: 'Testing project deleted',
        description: `"${item.bondName}" has been deleted`,
        type: 'success',
      });
    } else {
      toaster.create({
        title: 'Delete failed',
        description: `Failed to delete "${item.bondName}"`,
        type: 'error',
      });
    }
  };

  const handleArchive = async (item: ProjectData) => {
    if (item.status === 'archived') {
      // Unarchive
      await unarchiveProject(item.id);
      console.log(`Project unarchived: ${item.bondName} (${item.id})`);
    } else {
      // Archive
      await archiveProject(item.id);
      console.log(`Project archived: ${item.bondName} (${item.id})`);
    }
  };

  // Handle clone (admin only)
  const handleClone = (item: ProjectData) => {
    setCloneProject(item);
  };

  // Handle clone modal close
  const handleCloneModalClose = () => {
    setCloneProject(null);
  };

  // Get loadProject function for smart navigation
  const loadProject = useProjectStore((state) => state.loadProject);

  // Handle row click - smart navigate based on project completeness
  const handleRowClick = useCallback(
    async (item: ProjectData) => {
      // Check if project is cached (has been loaded before)
      let storeProject: StoreProjectData | undefined = cachedProjects[item.id];

      // If not cached, load it first for smart navigation
      if (!storeProject) {
        storeProject = (await loadProject(item.id)) ?? undefined;
      }

      const route = getSmartRoute(storeProject);
      navigate(`/projects/${item.id}/${route}`);
    },
    [cachedProjects, loadProject, navigate]
  );

  // Handle new project button click
  const handleNewProject = () => {
    setIsCreateModalOpen(true);
  };

  // Handle edit modal close
  const handleEditModalClose = () => {
    setEditProject(null);
  };

  // Filter data based on search query - memoized to prevent dependency issues
  const filterDataBySearch = useCallback(
    (data: ProjectData[]) => {
      if (!searchQuery) return data;

      const query = searchQuery.toLowerCase();
      return data.filter(
        (project) =>
          project.bondName.toLowerCase().includes(query) ||
          project.clientName.toLowerCase().includes(query)
      );
    },
    [searchQuery]
  );

  // Data for each tab - memoized to prevent infinite re-renders
  const activeData = useMemo(
    () => filterDataBySearch(projects.filter((p) => p.status === 'active')),
    [projects, filterDataBySearch]
  );

  const archivedData = useMemo(
    () => filterDataBySearch(projects.filter((p) => p.status === 'archived')),
    [projects, filterDataBySearch]
  );

  const allData = useMemo(() => filterDataBySearch(projects), [projects, filterDataBySearch]);

  // Testing projects data (super admin only)
  const testingData = useMemo(
    () => filterDataBySearch(testingProjects),
    [testingProjects, filterDataBySearch]
  );

  // Dynamic tab counts
  const activeCount = activeData.length;
  const archivedCount = archivedData.length;
  const testingCount = testingData.length;

  // Show loading spinner until we've completed at least one load attempt
  if (!hasLoadedList) {
    return <PageLoadingSpinner message="Loading projects..." />;
  }

  return (
    <Stack direction="column" w="full" h="100%" alignItems="stretch" gap={0}>
      {/* Header bar with dark background */}
      <Box py={LAYOUT_SPACING.CONTENT_GAP} px={LAYOUT_SPACING.PAGE_PADDING}>
        <HStack gap={LAYOUT_SPACING.BUTTON_GAP} w="full" justify="flex-end">
          <Input
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            maxW="500px"
            borderRadius={LAYOUT_SPACING.BORDER_RADIUS}
            bg="white"
            border="none"
          />
          <PrimaryButton leftIcon={<Plus size={16} />} onClick={handleNewProject}>
            New Project
          </PrimaryButton>
        </HStack>
      </Box>

      {/* Main content */}
      <Box p={LAYOUT_SPACING.PAGE_PADDING}>
        <Box
          bg="white"
          borderRadius={LAYOUT_SPACING.BORDER_RADIUS_LG}
          overflow="hidden"
          boxShadow="sm"
        >
          <Tabs.Root
            variant="line"
            value={selectedTab}
            onValueChange={(details: { value: string }) => setSelectedTab(details.value)}
          >
            <Tabs.List>
              <Box
                pt={LAYOUT_SPACING.PAGE_PADDING}
                px={LAYOUT_SPACING.PAGE_PADDING}
                display="flex"
                gap={LAYOUT_SPACING.CONTENT_GAP}
              >
                <Tabs.Trigger
                  value="active"
                  px={4}
                  py={2}
                  _selected={{ color: 'neutral.status.green' }}
                >
                  Active Projects{' '}
                  <StatusBadge size="sm" ml={2} status="active" count={activeCount} />
                </Tabs.Trigger>

                <Tabs.Trigger value="all" px={4} py={2} _selected={{ color: 'blue.500' }}>
                  All Projects <StatusBadge size="sm" ml={2} status="blue" count={allData.length} />
                </Tabs.Trigger>

                <Tabs.Trigger value="archived" px={4} py={2} _selected={{ color: 'gray.500' }}>
                  Archived <StatusBadge size="sm" ml={2} status="archived" count={archivedCount} />
                </Tabs.Trigger>

                {/* Values Testing tab - super admin only */}
                {isSuperAdmin && (
                  <Tabs.Trigger
                    value="values-testing"
                    px={4}
                    py={2}
                    _selected={{ color: 'purple.500' }}
                  >
                    Values Testing{' '}
                    <StatusBadge size="sm" ml={2} status="purple" count={testingCount} />
                  </Tabs.Trigger>
                )}
              </Box>
            </Tabs.List>

            <Box
              px={LAYOUT_SPACING.PAGE_PADDING}
              pb={LAYOUT_SPACING.PAGE_PADDING}
              pt={LAYOUT_SPACING.ELEMENT_GAP}
            >
              {(() => {
                // Determine which data to use based on selected tab
                let tabData: ProjectData[] = [];
                let emptyMessage = '';

                switch (selectedTab) {
                  case 'active':
                    tabData = activeData;
                    emptyMessage = searchQuery
                      ? 'No active projects match your search.'
                      : 'No active projects found.';
                    break;
                  case 'archived':
                    tabData = archivedData;
                    emptyMessage = searchQuery
                      ? 'No archived projects match your search.'
                      : 'No archived projects found.';
                    break;
                  case 'values-testing':
                    tabData = testingData;
                    emptyMessage = searchQuery
                      ? 'No testing projects match your search.'
                      : 'No testing projects found. Copy a project as testing from the Proposal page.';
                    break;
                  default:
                    tabData = allData;
                    emptyMessage = searchQuery
                      ? 'No projects match your search.'
                      : 'No projects found.';
                    break;
                }

                return tabData.length > 0 ? (
                  <ReusableTable
                    columns={projectColumns}
                    data={tabData}
                    onEdit={handleEdit}
                    // Testing projects use a separate delete handler (different API endpoint)
                    onDelete={
                      selectedTab === 'values-testing' ? handleValuesTestingDelete : handleDelete
                    }
                    onArchive={handleArchive}
                    onClone={isSuperAdmin ? handleClone : undefined}
                    onRowClick={handleRowClick}
                  />
                ) : (
                  <Box py={10} textAlign="center" color="gray.500">
                    {emptyMessage}
                  </Box>
                );
              })()}
            </Box>
          </Tabs.Root>
        </Box>
      </Box>

      {/* Create Project Modal */}
      <ProjectModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} />

      {/* Edit Project Modal */}
      <ProjectModal
        isOpen={!!editProject}
        onClose={handleEditModalClose}
        project={
          editProject
            ? {
                id: editProject.id,
                name: editProject.bondName,
                clientName: editProject.clientName !== '-' ? editProject.clientName : undefined,
                targetCompletionDate: editProject.targetCompletionDate,
              }
            : undefined
        }
      />

      {/* Clone Project Modal (admin only) */}
      <CloneProjectModal
        isOpen={!!cloneProject}
        onClose={handleCloneModalClose}
        project={cloneProject}
      />
    </Stack>
  );
};

export default HomePage;
